import { BKJLoadingSpinner } from '@bookjane2/bookjane-design-library';
import {
  ActivityTabTableStyled,
  ActivityTabTableWrapper,
  TableHeaderCell,
} from 'components/ActivityTab/ActivityTab.styled';
import { ActivityTabRow } from 'components/ActivityTab/ActivityTabRow';
import { ActivityTabRowPropsProvider } from 'components/ActivityTab/ActivityTabRow.props.context';
import {
  ActivitiesDataContext,
  ActivitiesDataProvider,
  useActivitiesDataContext,
} from 'components/ActivityTab/ActivityTab.data.context';
import { Flex } from 'components/Flex';
import { InfiniteScrollLoading } from 'components/InfiniteScrollLoading';
import { FC, Fragment, useMemo } from 'react';
import { useTranslation } from 'hooks/useTranslation';
import { MountTransition } from 'routes';
import { UserType } from 'types/activities.types';
import { ActivitiesType } from 'types/users.types';
import type { ShiftModalDetailsData } from 'modals/ShiftModal/ShiftModal.constants';

const ActivityTabInner: FC<{ timeZone?: string }> = ({ timeZone, children }) => {
  const { t } = useTranslation();
  const { activities, isLoading, hasNextPage, loadMore } = useActivitiesDataContext();

  return (
    <Fragment>
      <ActivityTabTableWrapper>
        <MountTransition>
          <ActivityTabTableStyled>
            <TableHeaderCell>{t('common:TIME')}</TableHeaderCell>
            <TableHeaderCell>{t('common:ACTIVITY')}</TableHeaderCell>
            <TableHeaderCell>{t('dashboard:BY_USER')}</TableHeaderCell>

            {activities.map(({ createdAt, event, user, metadata }, index) => {
              const userDetails = { ...user } as UserType;

              return (
                <ActivityTabRowPropsProvider
                  timeZone={timeZone}
                  key={createdAt + event.value}
                  createdAt={createdAt}
                  event={event}
                  userDetails={userDetails}
                  index={index}
                  metadata={metadata ?? null}
                >
                  <ActivityTabRow />
                </ActivityTabRowPropsProvider>
              );
            })}

            {hasNextPage && (
              <InfiniteScrollLoading onViewportEnter={loadMore} style={{ gridColumn: '1 / -1' }} />
            )}
          </ActivityTabTableStyled>
          {isLoading && (
            <Flex justifyContent="center" padding="24px">
              <BKJLoadingSpinner variant="Default" />
            </Flex>
          )}
        </MountTransition>
      </ActivityTabTableWrapper>
      {children}
    </Fragment>
  );
};

type ActivityTabProps =
  | { data: ShiftModalDetailsData; activities?: undefined; timeZone?: string }
  | { activities: ActivitiesType[]; data?: undefined; timeZone?: string };

const StaticActivitiesProvider: FC<{ activities: ActivitiesType[] }> = ({
  activities,
  children,
}) => {
  const value = useMemo(
    () => ({ activities, isLoading: false, hasNextPage: false, loadMore: () => {} }),
    [activities],
  );
  return <ActivitiesDataContext.Provider value={value}>{children}</ActivitiesDataContext.Provider>;
};

export const ActivityTab: FC<ActivityTabProps> = ({ data, activities, timeZone, children }) => {
  if (data?.order?.relayId) {
    return (
      <ActivitiesDataProvider orderId={data.order.relayId}>
        <ActivityTabInner timeZone={timeZone}>{children}</ActivityTabInner>
      </ActivitiesDataProvider>
    );
  }
  return (
    <StaticActivitiesProvider activities={activities ?? []}>
      <ActivityTabInner timeZone={timeZone}>{children}</ActivityTabInner>
    </StaticActivitiesProvider>
  );
};
